// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { push } from 'connected-react-router';
import compose from 'recompose/compose';
import { arrayOf, bool, func, shape, string, object } from 'prop-types';
import {
  Button,
  Collapse,
  Grid,
  Link,
  Typography,
  withStyles,
  Popover,
} from '@material-ui/core';
import { find, map, get } from 'lodash';
import queryString from 'query-string';

import { FormSelect, FormAutoCompleteCombo } from '../../../../components';
import { ApiAssets } from '../../../../pageComponents';
import {
  getKeysResults,
} from '../../../../reducers/application';
import {
  fetchApplicationKeys,
} from '../../../../actions/application';
import {
  fetchApplications,
  fetchApis,
  fetchSwaggerFile,
  fetchApiDetails,
  fetchAssets,
} from "../../../../actions/products";
import {
  getAppsListResults,
  getApisListResults,
  getApiSwaggerData,
  getApiAssets,
  getApiDetails,
} from "../../../../reducers/products";
import { getConfig } from "../../../../reducers/portalConfig";
import initializeSwaggerUiBundle, { SWAGGER_UI_EL } from './swagger';
import styles from './styles';
import { hasCRUDPermissions } from '../../../../utils';
import { getI18n } from '../../../../utils/intl';
import Apis from './api';
import {
  ASSIGNED,
  PERMISSION_APIS_PRODUCT,
  PERMISSION_APPLICATION,
} from '../../../../constants';

export const Spec = (props) => {
  const {
    classes,
    apiSwaggerData,
    applicationAPIKeys,
    availableApps,
    fetchApplicationKeys,
    fetchSwaggerFile,
    productUuid,
    tabValue,
    apisList,
    apiInfo,
    assetsList,
    fetchAssets,
    fetchApiDetails,
    fetchApplications,
    config,
    userContext,
    location,
    push,
  } = props

  const [selectedApp, setSelectedApp] = useState('0');
  const [appList, setAppList] = useState([]);
  const [appSearchText, setAppSearchText] = useState('');
  const [selectedAppAPIKey, setSelectedAppAPIKey] = useState('0');
  const [selectionCollapsed, setSelectionCollapsed] = useState(false);
  const [apiUuid, setApiUuid] = useState('');
  const [apiDetails, setApiDetails] = useState({});
  const [apiSwagger, setApiSwagger] = useState({});
  const [assets, setAssets] = useState([]);
  const [isAssetOpen, setIsAssetOpen] = useState(false);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);

  const intl = getI18n(useIntl());
  const tenantId = get(config, 'portal.tenantPrefix');
  const assestsLink = `/api/${tenantId}/api-management/1.0/products/${productUuid}/apis/${apiUuid}/assets/archive`;
  const hasVisibleApi = hasCRUDPermissions(userContext, ['READ'], PERMISSION_APIS_PRODUCT);
  const hasCreateApp = hasCRUDPermissions(userContext, ['CREATE'], PERMISSION_APPLICATION);

  useEffect(() => {
    if (!hasVisibleApi && tabValue === 'products-details-spec-tab') {
      props.push('/404');
    }
  }, [tabValue]);

  useEffect(() => {
    if (productUuid && tabValue === 'products-details-spec-tab') {
      props.fetchApis({ uuid: productUuid, page: 0, size: 24, assign: ASSIGNED });
    }
  }, [productUuid, tabValue]);

  useEffect(() => {
    if (apiUuid) {
      fetchApplications({ size: 2000, uuid: productUuid, assign: ASSIGNED,
        page: 0, status: 'any',
      });
      fetchAssets(productUuid, apiUuid);
      fetchApiDetails(productUuid, apiUuid);
    }
    setAppSearchText('');
    setSelectedApp('0');
    setSelectedAppAPIKey('0');
  }, [apiUuid]);

  useEffect(() => {
    if(get(apiDetails, 'apiServiceType') === 'REST')
      fetchSwaggerFile(productUuid, apiUuid);
  }, [apiDetails])

  useEffect(() => {
    if (apiInfo) {
     setApiDetails(apiInfo);
    }
  }, [apiInfo]);

  useEffect(() => {
    const parsed = queryString.parse(location.search);
    const id = get(parsed, 'apiUuid');
    if(id) {
      setApiUuid(id);
    }
  }, [location]);

  useEffect(() => {
    if(tabValue === 'products-details-spec-tab') {
      const parsed = queryString.parse(location.search);
      const id = get(parsed, 'apiUuid');
      if (apisList && apisList.length > 0 & !id) {
        const uuid = get(apisList[0], 'apiUuid');
        push(`/admin/products/details/${productUuid}/spec?apiUuid=${uuid}`);
        setSelectedRows([uuid]);
      }
    }
  }, [apisList, location, tabValue]);

  useEffect(() => {
    if (assetsList) {
      setAssets(assetsList);
    }
  }, [assetsList]);

  useEffect(() => {
    if (availableApps) {
      setAppList([
        {
          name: intl.getI18nMessage('label.api.details.spec.select.application'),
          uuid: '0',
        },
        ...availableApps,
      ])
    }
  }, [availableApps]);

  useEffect(() => {
    if (appSearchText === '') {
      setSelectedApp('0');
    }
  }, [appSearchText]);

  useEffect(() => {
    if (selectedApp !== '0') {
      fetchApplicationKeys(selectedApp, 0, 200, 'SECRET');
    }
    setSelectedAppAPIKey('0');
  }, [selectedApp]);

  useEffect(() => {
    if (apiSwaggerData) {
      setApiSwagger(apiSwaggerData);
    }
  }, [apiSwaggerData]);

  useEffect(() => {
    if (apiSwagger) {
      const data = get(apiSwagger, 'content') ?
        JSON.parse(get(apiSwagger, 'content')) : '';
      if(get(apiDetails, 'apiServiceType') === 'REST') {
        initializeSwaggerUiBundle({
          api: apiDetails,
          apiKey: {},
          apiSwagger: data,
        });
      }
    }
  }, [apiSwagger, apiDetails]);

  useEffect(() => {
    const data = get(apiSwagger, 'content') ?
      JSON.parse(get(apiSwagger, 'content')) : '';
    let apiKeyDetails = {};
    if ((selectedApp !== '0') && (selectedAppAPIKey !== '0')) {
      apiKeyDetails = find(applicationAPIKeys, item => item.apiKey === selectedAppAPIKey);
    }
    initializeSwaggerUiBundle({
      api: apiDetails,
      apiKey: apiKeyDetails,
      apiSwagger: data,
    });
  }, [selectedApp, selectedAppAPIKey]);

  const onApiClick = (id) => {
    if(id !== apiUuid) {
      setApiDetails({});
      setApiSwagger({});
      setAssets([]);
      push(`/admin/products/details/${productUuid}/spec?apiUuid=${id}`);
      setSelectedRows([id]);
    }
  }

  const onFilterByAppChange = (event = { }) => {
    const value = get(event, 'target.value', '');
    value && setAppSearchText(value);
    fetchApplications({ size: 2000, uuid: productUuid, assign: ASSIGNED, name: value,
      page: 0, status: 'any' });
  };

  const onAppSelection = (event, value) => {
    const appUuid = get(value, 'uuid', '');
    const appName = get(value, 'name', '');
    setSelectedApp(appUuid);
    setAppSearchText(appName);
  };

  const renderApplicationsDropdown = () => {
    return (<FormAutoCompleteCombo
      fieldContainerClass={classes.selectionDropdown}
      data={appList}
      isMultiple={false}
      filterSelectedOptions={false}
      searchText={appSearchText}
      inputValue={appSearchText}
      onInputChange={onFilterByAppChange}
      onChange={(e, value) => onAppSelection(e, value)}
      id="api-spec-apps-select"
      labelId="api-spec-apps-select-label"
      name={intl.getI18nMessage('label.application')}
      value={selectedApp}
      inputLabel={intl.getI18nMessage('label.api.details.spec.select.application')}
    />);
  };

  const getApiKeys = () => map(applicationAPIKeys,
    item => ({ name: item.name, uuid: item.apiKey }));

  const renderAPIKeysDropdown = () => {
    return (<FormSelect
      fieldContainerClass={classes.selectionDropdown}
      data={getApiKeys()}
      defaultText={intl.getI18nMessage('label.api.details.spec.select.application.apikey')}
      disabled={selectedApp === '0'}
      handleChange={setSelectedAppAPIKey}
      id="api-spec-apps-apikey-select"
      labelId="api-spec-apps-apikey-select-label"
      name={intl.getI18nMessage('label.apikey')}
      noNativeSelect
      value={selectedAppAPIKey}
      helperText={intl.getI18nMessage('label.api.details.spec.select.application.apikey.helpertext')}
    />);
  };

  const popOverAssetDetails = () => {
    return (
      <Popover
        id={'asset-popover'}
        className={classes.assestPopOverContainer}
        open={isAssetOpen}
        onClose={()=> setIsAssetOpen(false)}
      >
      <Typography className={classes.typography}>
        <ApiAssets
          apiAssets={assets}
          config={config}
          link={assestsLink}
          api={apiDetails}
          hasTitle={false}
        />
      </Typography>
    </Popover>
    )
  };

  const popOverDescriptionDetails = () => {
    return (
      <Popover
        id={'description-popover'}
        className={classes.descriptionPopOverContainer}
        open={isDescriptionOpen}
        onClose={()=> setIsDescriptionOpen(false)}
      >
      <Typography className={classes.typography}>
        {get(apiDetails, 'description')}
      </Typography>
    </Popover>
    )
  }

  return (
    <Grid
      container
      data-apim-test="product-details-spec-container"
      data-layer7-test="product-details-spec-container"
      id="product-details-spec-container"
      maxWidth={false}
    >
      <Grid item md={3} className={classes.apiContainer}>
        <Apis
          {...props}
          onApiClick={onApiClick}
          selectedRows={selectedRows}
        />
      </Grid>
      <Grid item md={9} className={classes.specDetails}>
        <Grid container md={12} className={classes.apiDetailsContainer}
          data-apim-test="product-spec-api-details-container"
          id="product-spec-api-details-container"
        >
          <span className={classes.headerNameContainer}>
            <h2>{get(apiDetails, 'name')}</h2>
          </span>
          <span className={classes.headerContainer}>
            <Button
              id="assets-button"
              data-apim-test="assets-button"
              color="primary"
              variant="outlined"
              className={classes.cancelButton}
              onClick={() => setIsAssetOpen(true)}
            >
              {'Assests'}
            </Button>
            {popOverAssetDetails()}
          </span>
          <span className={classes.headerContainer}>
            <Button
              id="description-button"
              data-apim-test="description-button"
              color="primary"
              variant="outlined"
              className={classes.cancelButton}
              onClick={() => setIsDescriptionOpen(true)}
            >
              {'Description'}
            </Button>
            {popOverDescriptionDetails()}
          </span>
        </Grid>
        {(get(apiDetails, 'apiServiceType') === 'REST') &&
          <>
            <Grid item md={12} className={classes.authDetailsContainer}
              data-apim-test="product-spec-auth-details-container"
              id="product-spec-auth-details-container"
            >
              <Typography id="product-spec-connection-details" variant="h4">
                {intl.getI18nMessage('label.api.details.authentication.details')}
              </Typography>
              <Button
                classes={{
                  root: classes.btnRoot,
                  label: classes.btnLabel,
                }}
                className={classes.collapseLink}
                onClick={() => setSelectionCollapsed(!selectionCollapsed)}
              >
                {
                  selectionCollapsed ?
                  intl.getI18nMessage('label.api.details.authentication.details.show')
                  :
                  intl.getI18nMessage('label.api.details.authentication.details.hide')
                }
              </Button>
            </Grid>
            <Collapse className={classes.collapse} in={!selectionCollapsed}>
              <Grid
                container
                md={12}
                className={classes.appContainer}
              >
                <Grid item md={5} sm={5} xs={12} className={classes.filterContainer}>
                  {renderApplicationsDropdown()}
                </Grid>
                <Grid item md={5} sm={5} xs={12}>
                  {renderAPIKeysDropdown()}
                </Grid>
                <Grid item md={2} sm={2} xs={12}>
                  {hasCreateApp &&
                    <div>
                      <Link
                        className={classes.applicationLink}
                        href={'/admin/console/applications/add'}
                      >
                        {intl.getI18nMessage('label.api.details.spec.create.application')}
                      </Link>
                    </div>
                  }
                </Grid>
              </Grid>
            </Collapse>
              <div id={SWAGGER_UI_EL} className={classes.swaggerContainer}
                data-apim-test="product-details-swagger-container"
              />
          </>
        }
      </Grid>
    </Grid>
  );
};

const mapStateToProps = state => ({
  apiSwaggerData: getApiSwaggerData(state),
  applicationAPIKeys: getKeysResults(state),
  availableApps: getAppsListResults(state),
  apisList: getApisListResults(state),
  apiInfo: getApiDetails(state),
  assetsList: getApiAssets(state),
  config: getConfig(state),
});

const mapDispatchToProps = {
  fetchApplicationKeys,
  fetchApplications,
  fetchSwaggerFile,
  fetchApis,
  fetchApiDetails,
  fetchAssets,
  push,
};

Spec.propTypes = {
  productUuid: string,
  apiSwaggerData: object,
  applicationAPIKeys: arrayOf(object),
  availableApps: arrayOf(object),
  classes: shape({}),
  fetchApplicationKeys: func,
  fetchApplications: func,
  fetchSwaggerFile: func,
  fetchApis: func,
  isCurrentTab: bool,
  tabValue: string,
  apisList: arrayOf(object),
  apiInfo: object,
  assetsList: arrayOf(object),
  config: object,
  fetchAssets: func,
  fetchApiDetails: func,
  push: func,
  userContext: shape({}),
  location: shape({}),
};

Spec.displayName = 'Spec';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
  )(Spec);